import axios from "axios";
import API_BASE_URL, { IS_LOCAL_API } from "../config/apiConfig";
import { getAccessToken } from "../util/auth";
import { getApiErrorMessage } from "./apiClient";
import { API_GATEWAY_MAX_UPLOAD_BYTES } from "../util/subtitleHelpers";

const CAPTION_BASE = `${API_BASE_URL}/caption-publishing`;

const getMultipartAuthConfig = () => {
  const token = getAccessToken();

  return {
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
    },
  };
};

const getJsonAuthConfig = () => {
  const token = getAccessToken();

  return {
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
    },
  };
};

export const requestCaptionUploadUrl = async ({ fileName, contentType, fileSizeBytes }) => {
  const response = await axios.post(
    `${CAPTION_BASE}/jobs/upload-url`,
    { fileName, contentType, fileSizeBytes },
    getJsonAuthConfig(),
  );

  return response.data;
};

export const uploadVideoToPresignedUrl = async (uploadUrl, videoFile, contentType) => {
  await axios.put(uploadUrl, videoFile, {
    headers: {
      "Content-Type": contentType || videoFile.type || "application/octet-stream",
    },
    timeout: 300000,
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
  });
};

export const completeCaptionUpload = async ({ jobId, fileName, title, language, durationSeconds }) => {
  const response = await axios.post(
    `${CAPTION_BASE}/jobs/complete`,
    { jobId, fileName, title, language, durationSeconds },
    getJsonAuthConfig(),
  );

  return response.data;
};

export const createCaptionJobDirect = async (videoFile, options = {}) => {
  const formData = new FormData();
  formData.append("video", videoFile);

  if (options.language) {
    formData.append("language", options.language);
  }

  if (options.title) {
    formData.append("title", options.title);
  }

  if (Number(options.durationSeconds) > 0) {
    formData.append("durationSeconds", String(options.durationSeconds));
  }

  const response = await axios.post(`${CAPTION_BASE}/jobs`, formData, {
    ...getMultipartAuthConfig(),
    timeout: IS_LOCAL_API ? 300000 : 120000,
  });

  return response.data;
};

export const createCaptionJob = async (videoFile, options = {}) => {
  const fileSizeBytes = Number(videoFile?.size) || 0;
  const notifyPhase = (phase) => {
    if (typeof options.onUploadPhase === "function") {
      options.onUploadPhase(phase);
    }
  };

  if (!IS_LOCAL_API && fileSizeBytes > API_GATEWAY_MAX_UPLOAD_BYTES) {
    notifyPhase("requesting_upload_url");
    const uploadTarget = await requestCaptionUploadUrl({
      fileName: options.title || videoFile.name,
      contentType: videoFile.type || "application/octet-stream",
      fileSizeBytes,
    });

    notifyPhase("uploading_to_storage");
    await uploadVideoToPresignedUrl(
      uploadTarget.uploadUrl,
      videoFile,
      uploadTarget.contentType || videoFile.type,
    );

    notifyPhase("completing_upload");
    return completeCaptionUpload({
      jobId: uploadTarget.jobId,
      fileName: uploadTarget.fileName || options.title || videoFile.name,
      title: options.title || videoFile.name,
      language: options.language,
      durationSeconds: options.durationSeconds,
    });
  }

  notifyPhase("uploading_direct");
  return createCaptionJobDirect(videoFile, options);
};

export const formatCaptionUsageSummary = (usage = {}) => {
  const usedSeconds = Number(usage.usedDurationSeconds) || 0;
  const maxHours = Number(usage.maxTotalDurationHours) || 3;
  const usedMinutes = Math.round(usedSeconds / 60);
  const usedHours = Math.round((usedSeconds / 3600) * 10) / 10;
  const usedLabel =
    usedSeconds >= 3600
      ? `${usedHours} hour${usedHours === 1 ? "" : "s"}`
      : `${usedMinutes} minute${usedMinutes === 1 ? "" : "s"}`;

  return `You have used ${usedLabel} of ${maxHours} hours on the free plan.`;
};

export const CAPTION_UPLOAD_PHASE_MESSAGES = {
  requesting_upload_url: "Preparing secure upload...",
  uploading_to_storage: "Uploading to secure storage...",
  completing_upload: "Finishing upload and validating your video...",
  uploading_direct: "Uploading video and starting speech transcription...",
};

export const fetchCaptionJob = async (jobId) => {
  const response = await axios.get(`${CAPTION_BASE}/jobs/${jobId}`, {
    ...getMultipartAuthConfig(),
  });

  return response.data;
};

export const fetchCaptionJobVideo = async (jobId) => {
  const response = await axios.get(`${CAPTION_BASE}/jobs/${jobId}/video`, {
    ...getMultipartAuthConfig(),
    responseType: "blob",
    timeout: 120000,
  });

  return response.data;
};

export const pollCaptionJob = async (jobId, { intervalMs = 3000, maxAttempts = 120, onUpdate } = {}) => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const job = await fetchCaptionJob(jobId);

    if (typeof onUpdate === "function") {
      onUpdate(job);
    }

    if (job.status === "completed" || job.status === "failed") {
      return job;
    }

    await new Promise((resolve) => {
      setTimeout(resolve, intervalMs);
    });
  }

  throw new Error("Caption generation timed out. Please try again.");
};

export const getCaptionApiErrorMessage = (error, fallback) =>
  getApiErrorMessage(error, fallback);

export const fetchMyCaptionJobs = async () => {
  const response = await axios.get(`${CAPTION_BASE}/jobs/mine`, getMultipartAuthConfig());
  return response.data?.items || response.data || [];
};

export const fetchAllCaptionJobs = async () => {
  const response = await axios.get(`${CAPTION_BASE}/jobs`, getMultipartAuthConfig());
  return response.data?.items || response.data || [];
};

export const downloadCaptionSubtitle = async (jobId, format = "srt", variant = "default") => {
  const response = await axios.get(
    `${CAPTION_BASE}/jobs/${jobId}/subtitles.${format}?variant=${variant}`,
    {
      ...getMultipartAuthConfig(),
      responseType: "blob",
    },
  );

  return response.data;
};

export const recordConsumptionAlert = async (alert) => {
  const response = await axios.post(`${CAPTION_BASE}/consumption-alerts`, alert, getMultipartAuthConfig());
  return response.data;
};

export const fetchAllConsumptionAlerts = async () => {
  const response = await axios.get(`${CAPTION_BASE}/consumption-alerts`, getMultipartAuthConfig());
  return response.data?.items || response.data || [];
};

export const fetchMyConsumptionAlerts = async () => {
  const response = await axios.get(`${CAPTION_BASE}/consumption-alerts/mine`, getMultipartAuthConfig());
  return response.data?.items || response.data || [];
};

export const fetchMyCaptionUsage = async () => {
  const response = await axios.get(`${CAPTION_BASE}/usage/mine`, getMultipartAuthConfig());
  return response.data;
};
